/**
 * BUSINESS-100 (jawaban Owner "100 setuju", 25 Okt 2026) — layar Kanal WhatsApp:
 *   1. "WhatsApp penting yang belum sampai": tugas CS (tautan bayar, Ruang Pesanan, FA siap, kwitansi, pesanan
 *      selesai) yang gagal atau 15 menit belum sampai. Nomor, isi pesan, dan tautan bisa disalin; CS mencatat
 *      hasilnya: sudah dihubungi / nomor salah / nomor tidak aktif (nomor bermasalah ikut ditandai di data customer).
 *      1 jam kerja belum ditangani → Kepala Cabang ikut diberi tahu.
 *   2. "Status WhatsApp ke customer": 7 hari terakhir, tiap pesan dengan tahap + waktunya (T45).
 * Server: `backend/routers/wa_status.py` (Owner · KC · CS; KC/CS hanya cabangnya sendiri).
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { formatDateTime } from "@/lib/format";
import { konfirmasi } from "@/lib/dialogAplikasi";
import { BusyButton } from "@/components/etams/BusyButton";
import { Paginasi } from "@/components/etams/PalangDaftar";
import { useDaftar } from "@/hooks/useDaftar";
import { Input } from "@/components/ui/input";
import { BarisStatusWa, LencanaStatusWa, WaktuStatusWa } from "@/components/etams/StatusWaCustomer";
import { rujukanStatusWa } from "@/lib/statusWa";

function salin(teks, pesan = "Disalin.") {
  try {
    navigator.clipboard.writeText(teks);
    toast.success(pesan);
  } catch (e) {
    toast.error("Browser menolak menyalin. Salin teksnya dari layar.");
  }
}

const PILIHAN = [
  ["SUDAH_DIHUBUNGI", "Sudah dihubungi"],
  ["NOMOR_SALAH", "Nomor salah"],
  ["NOMOR_TIDAK_AKTIF", "Nomor tidak aktif"],
];

function KartuTugas({ x, muatUlang }) {
  const [catatan, setCatatan] = useState("");
  const [sibuk, setSibuk] = useState(null);
  const catat = async (hasil, label) => {
    if (hasil !== "SUDAH_DIHUBUNGI") {
      const ok = await konfirmasi(
        `Tandai nomor ${x.nomor} sebagai "${label.toLowerCase()}"?\n\nTandanya tampil di data customer sampai ` +
        "nomornya dibetulkan.", { judul: label, ya: "Ya, tandai" });
      if (!ok) return;
    }
    setSibuk(hasil);
    try {
      await api.post(`/wa-status/pesan/${x.id}/tindak-lanjut`, { hasil, catatan: catatan.trim() || null });
      toast.success(`Hasil dicatat: ${label}.`);
      muatUlang();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Hasil gagal dicatat."));
    } finally {
      setSibuk(null);
    }
  };
  return (
    <div className="rounded-lg border border-destructive/40 p-3 text-sm space-y-1.5" data-testid="wa-tugas-baris">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{x.jenis}</span>
        <LencanaStatusWa item={x} />
        {x.cabang && <span className="text-xs text-muted-foreground">Cabang {x.cabang}</span>}
        {x.nomor_pesanan && <span className="text-xs text-muted-foreground">· {x.nomor_pesanan}</span>}
      </div>
      <p>
        {x.customer?.nama || "Customer"} —{" "}
        <span className="font-medium">{x.nomor}</span>{" "}
        <button type="button" className="text-xs underline" onClick={() => salin(x.nomor, "Nomor disalin.")}
                data-testid="wa-tugas-salin-nomor">Salin nomor</button>
      </p>
      {x.alasan && <p className="text-xs">{x.alasan}</p>}
      {x.ulang_paling_cepat && (
        <p className="text-xs text-destructive">
          Jangan kirim ulang lewat WhatsApp sebelum {formatDateTime(x.ulang_paling_cepat)} — hubungi lewat telepon.
        </p>
      )}
      <p className="text-xs text-muted-foreground">Hubungi customer lewat telepon atau kirim ulang isi pesan ini.</p>
      <div className="rounded-md bg-muted/40 p-2">
        <p className="whitespace-pre-wrap break-words">{x.isi || "—"}</p>
        {(x.tautan || []).map((u) => (
          <p key={u} className="break-all text-xs">
            {u}{" "}
            <button type="button" className="underline" onClick={() => salin(u, "Tautan disalin.")}
                    data-testid="wa-tugas-salin-tautan">Salin tautan</button>
          </p>
        ))}
        <button type="button" className="mt-1 text-xs underline" onClick={() => salin(x.isi || "", "Isi pesan disalin.")}
                data-testid="wa-tugas-salin-isi">Salin isi pesan</button>
      </div>
      <WaktuStatusWa waktu={x.waktu} />
      <p className="text-xs text-muted-foreground">Menjadi tugas {formatDateTime(x.tugas?.dibuat)}</p>
      <Input className="h-8 text-xs" placeholder="Catatan (boleh kosong)" value={catatan} maxLength={300}
             onChange={(e) => setCatatan(e.target.value)} data-testid="wa-tugas-catatan" />
      <div className="flex flex-wrap gap-2">
        {PILIHAN.map(([kode, label]) => (
          <BusyButton key={kode} size="sm" variant={kode === "SUDAH_DIHUBUNGI" ? "default" : "outline"}
                      busy={sibuk === kode} disabled={!!sibuk} onClick={() => catat(kode, label)}
                      data-testid={`wa-tugas-${kode.toLowerCase()}`}>{label}</BusyButton>
        ))}
      </div>
    </div>
  );
}

export function DaftarTugasWa({ onBerubah } = {}) {
  const [items, setItems] = useState([]);
  const [galat, setGalat] = useState("");
  const muat = useCallback(async () => {
    setGalat("");
    try {
      const r = await api.get("/wa-status/tugas");
      setItems(r.data.items || []);
    } catch (e) {
      setGalat(apiErrorMessage(e, "Tugas WhatsApp gagal dimuat."));
    }
  }, []);
  useEffect(() => { muat(); }, [muat]);
  // T62: sesudah hasil dicatat, daftar status + antrean di layar yang sama ikut dimuat ulang.
  const sesudahCatat = useCallback(() => { muat(); if (onBerubah) onBerubah(); }, [muat, onBerubah]);
  const daftar = useDaftar(items, { kunciCari: ["nomor", "jenis", "cabang", "nomor_pesanan", "isi"], awalan: "tugas_" });
  return (
    <div className="space-y-3" data-testid="wa-tugas">
      {galat && <p className="text-sm text-destructive">{galat}</p>}
      {!galat && items.length === 0 && (
        <p className="text-sm text-muted-foreground">Tidak ada WhatsApp penting yang menunggu ditangani.</p>
      )}
      {daftar.tampil.map((x) => <KartuTugas key={x.id} x={x} muatUlang={sesudahCatat} />)}
      <Paginasi daftar={daftar} testId="wa-tugas-paginasi" />
    </div>
  );
}

export function DaftarStatusWaTerbaru({ versi = 0 } = {}) {
  const [items, setItems] = useState([]);
  const [galat, setGalat] = useState("");
  useEffect(() => {
    let hidup = true;
    api.get("/wa-status/terbaru")
      .then((r) => { if (hidup) setItems(r.data.items || []); })
      .catch((e) => { if (hidup) setGalat(apiErrorMessage(e, "Status WhatsApp gagal dimuat.")); });
    return () => { hidup = false; };
  }, [versi]);
  const daftar = useDaftar(items, { kunciCari: ["jenis", "label", "nomor", "nomor_pesanan"], awalan: "status_" });
  return (
    <div className="space-y-2" data-testid="wa-status-terbaru">
      {galat && <p className="text-sm text-destructive">{galat}</p>}
      {!galat && items.length === 0 && <p className="text-sm text-muted-foreground">Belum ada WhatsApp ke customer 7 hari terakhir.</p>}
      {daftar.tampil.map((x) => <BarisStatusWa key={x.id} item={x} rujukan={rujukanStatusWa(x)} />)}
      <Paginasi daftar={daftar} testId="wa-status-terbaru-paginasi" />
    </div>
  );
}
